/**
 * Format school year data with readable names
 * @param {Array} schoolYears - Array of school year objects with start and end dates
 * @returns {Array} Formatted school years with name property
 */
export function formatSchoolYears(schoolYears) {
    return schoolYears.map((sc) => {
        const start = new Date(sc.start);
        const end = new Date(sc.end);
        const startM =
            start.getMonth() < 9
                ? "0" + (start.getMonth() + 1)
                : "" + (start.getMonth() + 1);
        const endM =
            end.getMonth() < 9
                ? "0" + (end.getMonth() + 1)
                : "" + (end.getMonth() + 1);
        return {
            ...sc,
            name:
                startM +
                "-" +
                start.getFullYear() +
                " / " +
                endM +
                "-" +
                end.getFullYear(),
        };
    });
}

/**
 * Get the current active school year ID based on current date
 * @param {Array} schoolYears - Array of formatted school year objects
 * @returns {string} The ID of the current or nearest upcoming school year
 */
export function getCurrentScholarYearId(schoolYears) {
    const currentDate = new Date();
    currentDate.setHours(12, 0, 0, 0);

    let nearestUpcoming = null;

    for (const schoolYear of schoolYears) {
        const startDate = new Date(schoolYear.start);
        const endDate = new Date(schoolYear.end);

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);

        if (currentDate >= startDate && currentDate <= endDate) {
            return schoolYear._id;
        }

        if (startDate > currentDate) {
            if (!nearestUpcoming || startDate < new Date(nearestUpcoming.start)) {
                nearestUpcoming = schoolYear;
            }
        }
    }

    if (nearestUpcoming) {
        return nearestUpcoming._id;
    }

    return schoolYears[schoolYears.length - 1]?._id || null;
}

/**
 * Setup building configuration after login
 * @param {Object} context - Vue component context (this)
 * @param {Object} building - Building object from login response
 * @param {Object} apiClient - Axios instance to configure
 * @returns {Promise<void>}
 */
export async function setupBuildingConfig(context, buildings, apiClient) {
    const building = Array.isArray(buildings) ? buildings[0] : buildings;

    await context.$store.commit("settings/CHANGE_SETTING", {
        setting: "activeBuilding",
        value: building.dbName,
    });

    if (building.base_url) {
        apiClient.defaults.baseURL = building.base_url + "/api/web";
        await context.$store.commit("settings/CHANGE_SETTING", {
            setting: "base_url",
            value: building.base_url,
        });
    }

    await context.$store.commit("settings/CHANGE_SETTING", {
        setting: "activeBuildingId",
        value: building._id,
    });

    const buildingsArray = Array.isArray(buildings) ? buildings : [buildings];
    await context.$store.commit("settings/CHANGE_SETTING", {
        setting: "image",
        value: buildingsArray.map((b) => ({
            name: b.name,
            db: b.dbName,
            dbName: b.dbName,
            headMaster: b.headMaster,
            logo: b.logo,
        })),
    });

    return building;
}

/**
 * Setup school years after login
 * @param {Object} context - Vue component context (this)
 * @param {Object} apiClient - Axios instance
 * @param {Function} store - Store instance (from 'store' package)
 * @returns {Promise<Object>} Object with formatted school years and active ID
 */
export async function setupSchoolYears(context, apiClient, store) {
    const response = await apiClient.post("/schoolaryears/filter", {
        query: { $query: {}, $orderby: { end: -1 } },
    });

    const formattedData = formatSchoolYears(response.data);
    const activeSchoolarYearId = getCurrentScholarYearId(formattedData);

    await context.$store.commit("settings/CHANGE_SETTING", {
        setting: "activeSchoolarYear",
        value: activeSchoolarYearId,
    });

    await context.$store.commit("settings/CHANGE_SETTING", {
        setting: "schoolarYears",
        value: formattedData,
    });

    await store.set("app.settings.activeSchoolarYear", activeSchoolarYearId);
    await store.set("activeSchoolarYear", activeSchoolarYearId);
    await store.set("SchoolarYear", activeSchoolarYearId);

    return {
        schoolYears: formattedData,
        activeSchoolarYearId,
    };
}

/**
 * Complete login flow - sets up building, school years, and navigates
 * @param {Object} context - Vue component context (this)
 * @param {Object} user - User object from login response
 * @param {string} resourcesUrl - Resources URL from login response
 * @param {Object} apiClient - Axios instance
 * @param {Function} store - Store instance (from 'store' package)
 * @param {Object} router - Vue Router instance
 * @param {Function} getFirstAccessPath - Function to get first accessible path based on user access
 * @returns {Promise<void>}
 */
export async function completeLoginFlow(
    context,
    user,
    resourcesUrl,
    apiClient,
    store,
    router,
    getFirstAccessPath
) {
    // Setup building configuration first (this sets activeBuilding in store)
    const building = await setupBuildingConfig(context, user.building, apiClient);

    // Wait for store updates to be processed
    await context.$nextTick();

    await store.set("DB", building.dbName);
    store.set("app.topbar.favs", user.fav || []);

    // Setup school years
    await setupSchoolYears(context, apiClient, store);

    // Wait again to ensure all state is updated
    await context.$nextTick();

    const path = getFirstAccessPath(user.role.access);

    // Final wait before navigation to ensure router guard has all data
    await context.$nextTick();

    router.replace(path).catch((err) => {
        if (err.name !== "NavigationDuplicated") {
            console.error("Navigation error:", err);
        }
    });
}

